import { useCallback } from 'react';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { useClientTranslation } from '@/lib/core/i18n';
import { cn } from '@/lib/core/utils';
import type { FavoriteListDto } from '@/services/platform/api/model';
import { SheetHeader } from '../../ui/SheetHeader';
import { WishlistProductPreview, WishlistProductPreviewProps } from '../ui/WishlistProductPreview';
import { WishlistRadioList } from '../ui/WishlistRadioList';

export interface AddToWishlistSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: WishlistProductPreviewProps;
  lists: FavoriteListDto[];
  /** When provided, the matching list is rendered as selected on open. */
  selectedListGuid?: string;
  /** Fires immediately when the user taps a list row. Single-select commit pattern. */
  onSelectList: (publicGuid: string) => void;
  /** Fires when the user taps the already-selected row, removing the item from favorites. */
  onDeselectList: (publicGuid: string) => void;
  /** Triggered by the "CREATE NEW LIST" CTA. */
  onCreateNewList: () => void;
}

export function AddToWishlistSheet({
  open,
  onOpenChange,
  product,
  lists,
  selectedListGuid,
  onSelectList,
  onDeselectList,
  onCreateNewList,
}: AddToWishlistSheetProps) {
  const t = useClientTranslation();

  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle>
      <DrawerContent className="flex max-h-[85dvh] flex-col overflow-hidden">
        <SheetHeader
          title={t('App.Shop.Wishlist.AddToWishlist')}
          right={
            <button
              type="button"
              aria-label={t('App.Labels.Close')}
              onClick={handleClose}
              className="flex size-11 cursor-pointer items-center justify-center"
            >
              <Icon name="xmark" width={24} height={24} className="text-jncquoi-neutral-1000" />
            </button>
          }
        />

        <div data-base-ui-swipe-ignore className="flex flex-1 flex-col overflow-y-auto">
          {lists.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 py-8 text-center">
              <Icon name="heart" width={24} height={24} className="text-jncquoi-neutral-1000" />
              <div className="flex flex-col gap-2 tracking-[0.5px]">
                <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] uppercase">
                  {t('App.Shop.Wishlist.EmptyStateTitle')}
                </p>
                <p className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[14px]">
                  {t('App.Shop.Wishlist.EmptyStateDescriptionLine1')}
                  <br />
                  {t('App.Shop.Wishlist.EmptyStateDescriptionLine2')}
                </p>
              </div>
            </div>
          ) : (
            <>
              <WishlistProductPreview {...product} />
              <div className="border-jncquoi-neutral-900 mx-4 mt-4 border-t-[0.5px]" />
              <WishlistRadioList
                lists={lists}
                value={selectedListGuid}
                onValueChange={onSelectList}
                onDeselect={onDeselectList}
              />
            </>
          )}
        </div>

        <div
          className={cn(
            'shrink-0 px-4 pb-10.5',
            lists.length === 0 ? 'border-jncquoi-neutral-400 border-t-[0.5px] pt-6' : 'pt-2.5'
          )}
        >
          {lists.length === 0 ? (
            <Button
              variant="primary-solid"
              palette="dark"
              size="md"
              onClick={onCreateNewList}
              className="w-full"
            >
              {t('App.Shop.Favorites.CreateListButton')}
            </Button>
          ) : (
            <Button variant="tertiary-link" size="md" onClick={onCreateNewList} className="w-full">
              {t('App.Shop.Wishlist.CreateNewList')}
            </Button>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
